import type { PackageInclusionRecord as OfferPackageInclusionRecord } from '~/hooks/apis/inclusions/useGetPackageInclusionsByOffer';

import { buildOfferBuilderPackages, getPackagePersistId } from '../OfferPackages/utils';
import {
  PACKAGE_OVERRIDE_SORT_ORDER_BASE,
  type PackageInclusionRecord,
  getInclusionCategory,
  getInclusionName,
  getInclusionPersistId,
  getPackageInclusionSortOrder,
  isCustomInclusion,
  isInclusionHidden,
  isPackageOverrideSortOrder,
} from '../utils';

export type { PackageInclusionRecord };

export interface MasterInclusion {
  id: string;
  assignedPackageIds: Array<string>;
  category: string;
  categoryId?: string | null;
  currency?: string;
  highlighted: boolean;
  inclusion: string;
  isCustom?: boolean;
  isHidden?: boolean;
  luxPlusOnly: boolean;
  persistId?: string;
  requiresBooking?: boolean;
  rrp?: number | null;
  type?: string;
  unit?: string;
}

export type MasterInclusionEdit = Pick<
  MasterInclusion,
  'category' | 'categoryId' | 'currency' | 'inclusion' | 'isHidden' | 'requiresBooking' | 'rrp' | 'type' | 'unit'
>;

export interface PackageOption {
  id: string;
  name: string;
}

export const TABLE_GRID_COLUMNS =
  '40px minmax(150px, 180px) minmax(260px, 1fr) 110px 130px 130px minmax(160px, 200px) 40px';

export function toMasterInclusionEdit(
  inclusion: MasterInclusion,
  overrides: Partial<MasterInclusionEdit> = {},
): MasterInclusionEdit {
  return {
    category: inclusion.category,
    categoryId: inclusion.categoryId,
    currency: inclusion.currency,
    inclusion: inclusion.inclusion,
    isHidden: inclusion.isHidden,
    requiresBooking: inclusion.requiresBooking,
    rrp: inclusion.rrp,
    type: inclusion.type,
    unit: inclusion.unit,
    ...overrides,
  };
}

type OfferPackageInclusionWithDisplayFields = OfferPackageInclusionRecord &
  PackageInclusionRecord & {
    inclusion: OfferPackageInclusionRecord['inclusion'] & NonNullable<PackageInclusionRecord['inclusion']>;
  };

export function getPackageOptions(packages?: Array<App.Package>): Array<PackageOption> {
  const packageIndexByReference = new Map(packages?.map((offerPackage, index) => [offerPackage, index]));

  return buildOfferBuilderPackages(packages ?? []).map((offerPackage) => ({
    id:
      getPackagePersistId(offerPackage.package) ?? `package-${packageIndexByReference.get(offerPackage.package) ?? 0}`,
    name: offerPackage.name,
  }));
}

export function filterPackageOptions(packageOptions: Array<PackageOption>, searchTerm: string) {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  if (!normalizedSearchTerm) {
    return packageOptions;
  }

  return packageOptions.filter((option) => option.name.toLowerCase().includes(normalizedSearchTerm));
}

export function selectPackageOptions(currentPackageIds: Array<string>, packageOptions: Array<PackageOption>) {
  return Array.from(new Set(currentPackageIds.concat(packageOptions.map((option) => option.id))));
}

export function clearPackageOptions(currentPackageIds: Array<string>, packageOptions: Array<PackageOption>) {
  const packageIdsToClear = new Set(packageOptions.map((option) => option.id));

  return currentPackageIds.filter((packageId) => !packageIdsToClear.has(packageId));
}

function getInclusionKey(inclusion: OfferPackageInclusionWithDisplayFields) {
  return getInclusionPersistId(inclusion) ?? getInclusionName(inclusion).trim().toLowerCase();
}

function getMedian(values: Array<number>) {
  if (values.length === 0) {
    return Number.MAX_SAFE_INTEGER;
  }

  const sortedValues = [...values].sort((a, b) => a - b);
  const middleIndex = Math.floor(sortedValues.length / 2);

  return sortedValues.length % 2 === 0
    ? (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2
    : sortedValues[middleIndex];
}

export function buildMasterInclusionsFromOffer(
  packageInclusions: Record<string, Array<OfferPackageInclusionRecord>> | undefined,
): Array<MasterInclusion> {
  if (!packageInclusions) {
    return [];
  }

  // A package with any override row is entirely untrustworthy as a master-order voter.
  const overriddenPackageIds = new Set(
    Object.entries(packageInclusions).flatMap(([packageId, inclusions]) =>
      inclusions.some((inclusion) => isPackageOverrideSortOrder(getPackageInclusionSortOrder(inclusion)))
        ? [packageId]
        : [],
    ),
  );

  const inclusionsById = new Map<
    string,
    {
      inclusion: MasterInclusion;
      sortOrderByPackageId: Map<string, number>;
      overriddenPackageIds: Set<string>;
    }
  >();

  Object.entries(packageInclusions).forEach(([packageId, inclusions]) => {
    inclusions.forEach((rawInclusion) => {
      // Package inclusion payloads can include extra display fields; missing values intentionally fall back to off.
      const inclusion = rawInclusion as OfferPackageInclusionWithDisplayFields;
      const inclusionName = getInclusionName(inclusion);

      if (!inclusionName || isInclusionHidden(inclusion)) {
        return;
      }

      const inclusionKey = getInclusionKey(inclusion);
      const existingInclusion = inclusionsById.get(inclusionKey);
      const rawSortOrder = getPackageInclusionSortOrder(rawInclusion);

      if (existingInclusion) {
        if (!existingInclusion.inclusion.assignedPackageIds.includes(packageId)) {
          existingInclusion.inclusion.assignedPackageIds.push(packageId);
        }

        existingInclusion.inclusion.highlighted =
          existingInclusion.inclusion.highlighted || inclusion.style === 'highlighted';
        existingInclusion.inclusion.luxPlusOnly = existingInclusion.inclusion.luxPlusOnly || !!inclusion.lux_plus_tier;

        if (rawSortOrder !== undefined) {
          existingInclusion.sortOrderByPackageId.set(packageId, rawSortOrder);
        }

        if (overriddenPackageIds.has(packageId)) {
          existingInclusion.overriddenPackageIds.add(packageId);
        }
        return;
      }

      inclusionsById.set(inclusionKey, {
        inclusion: {
          id: inclusionKey,
          assignedPackageIds: [packageId],
          category: getInclusionCategory(inclusion),
          categoryId: inclusion.inclusion.category_id,
          currency: inclusion.inclusion.currency,
          highlighted: inclusion.style === 'highlighted',
          inclusion: inclusionName,
          isCustom: isCustomInclusion(inclusion),
          isHidden: !!inclusion.inclusion.is_hidden,
          luxPlusOnly: !!inclusion.lux_plus_tier,
          persistId: getInclusionPersistId(inclusion),
          requiresBooking: !!inclusion.inclusion.requires_booking,
          rrp: inclusion.inclusion.rrp,
          type: inclusion.inclusion.type,
          unit: inclusion.inclusion.unit,
        },
        sortOrderByPackageId: rawSortOrder !== undefined ? new Map([[packageId, rawSortOrder]]) : new Map(),
        overriddenPackageIds: overriddenPackageIds.has(packageId) ? new Set([packageId]) : new Set(),
      });
    });
  });

  // A lone assigned package has nothing else to distrust its order against, so it counts as a real vote.
  const getMasterRank = (entry: {
    inclusion: MasterInclusion;
    sortOrderByPackageId: Map<string, number>;
    overriddenPackageIds: Set<string>;
  }) => {
    const isLoneOverriddenPackage =
      entry.inclusion.assignedPackageIds.length === 1 && entry.overriddenPackageIds.size === 1;

    const packageSortOrders: Array<number> = [];
    const overrideSortOrders: Array<number> = [];

    entry.sortOrderByPackageId.forEach((sortOrder, packageId) => {
      if (!entry.overriddenPackageIds.has(packageId) || isLoneOverriddenPackage) {
        packageSortOrders.push(isLoneOverriddenPackage ? sortOrder - PACKAGE_OVERRIDE_SORT_ORDER_BASE : sortOrder);
      } else {
        overrideSortOrders.push(sortOrder - PACKAGE_OVERRIDE_SORT_ORDER_BASE);
      }
    });

    if (packageSortOrders.length > 0) {
      return getMedian(packageSortOrders);
    }

    return overrideSortOrders.length > 0
      ? PACKAGE_OVERRIDE_SORT_ORDER_BASE + getMedian(overrideSortOrders)
      : Number.MAX_SAFE_INTEGER;
  };

  return Array.from(inclusionsById.values())
    .sort(
      (a, b) =>
        getMasterRank(a) - getMasterRank(b) ||
        a.inclusion.inclusion.localeCompare(b.inclusion.inclusion, undefined, { sensitivity: 'base' }),
    )
    .map(({ inclusion }) => inclusion);
}

export function buildPackageInclusionsFromMaster(
  masterInclusions: Array<MasterInclusion>,
  packageOptions: Array<PackageOption>,
  // Filters the output only — masterIndex still comes from the full list, so a partial save keeps true positions.
  changedInclusionIds?: ReadonlySet<string>,
): Record<string, Array<PackageInclusionRecord>> {
  return packageOptions.reduce<Record<string, Array<PackageInclusionRecord>>>((packageInclusions, packageOption) => {
    const assignedInclusions = masterInclusions
      .map((inclusion, masterIndex) => ({ inclusion, masterIndex }))
      .filter(
        ({ inclusion }) =>
          inclusion.inclusion.trim() &&
          inclusion.assignedPackageIds.includes(packageOption.id) &&
          (!changedInclusionIds || changedInclusionIds.has(inclusion.id)),
      )
      .map(({ inclusion, masterIndex }) => ({
        packageInclusion: {
          ...(inclusion.persistId ? { inclusion_id: inclusion.persistId } : { id: inclusion.id }),
          inclusion: {
            ...(inclusion.persistId ? { id: inclusion.persistId } : {}),
            name: inclusion.inclusion,
            category_id: inclusion.categoryId,
            currency: inclusion.currency,
            is_hidden: !!inclusion.isHidden,
            requires_booking: !!inclusion.requiresBooking,
            rrp: inclusion.rrp,
            type: inclusion.type,
            category: {
              name: inclusion.category,
            },
            unit: inclusion.unit,
          },
          lux_plus_tier: inclusion.luxPlusOnly ? 'base' : null,
          style: inclusion.highlighted ? 'highlighted' : null,
          // Preserves master list position as a gap-friendly sort_order across packages.
          sort_order: masterIndex + 1,
        },
      }));

    packageInclusions[packageOption.id] = assignedInclusions.map(({ packageInclusion }) => packageInclusion);

    return packageInclusions;
  }, {});
}

export function getChangedMasterInclusions(
  inclusions: Array<MasterInclusion>,
  changedInclusionIds: ReadonlySet<string>,
): Array<MasterInclusion> {
  return inclusions.filter((inclusion) => changedInclusionIds.has(inclusion.id));
}

export function preserveMasterInclusionOrder(
  currentInclusions: Array<MasterInclusion>,
  nextInclusions: Array<MasterInclusion>,
) {
  const nextInclusionsById = new Map(nextInclusions.map((inclusion) => [inclusion.id, inclusion]));

  return currentInclusions
    .flatMap((inclusion) => {
      const nextInclusion = nextInclusionsById.get(inclusion.id);

      if (!nextInclusion) {
        return [];
      }

      nextInclusionsById.delete(inclusion.id);

      return [nextInclusion];
    })
    .concat(Array.from(nextInclusionsById.values()));
}

export function getPackageInclusionIdsToUnassign(
  masterInclusions: Array<MasterInclusion>,
  packageInclusions: Record<string, Array<OfferPackageInclusionRecord>> | undefined,
): Array<string> {
  if (!packageInclusions) {
    return [];
  }

  const assignedPackageIdsByInclusionId = new Map(
    masterInclusions.flatMap((inclusion) =>
      inclusion.persistId ? [[inclusion.persistId, new Set(inclusion.assignedPackageIds)] as const] : [],
    ),
  );

  return Object.entries(packageInclusions).flatMap(([packageId, inclusions]) =>
    inclusions.flatMap((rawInclusion) => {
      const inclusion = rawInclusion as OfferPackageInclusionWithDisplayFields;
      const inclusionId = getInclusionPersistId(inclusion);
      const rowId = rawInclusion.id ? String(rawInclusion.id) : undefined;
      const assignedPackageIds = inclusionId ? assignedPackageIdsByInclusionId.get(inclusionId) : undefined;

      if (!inclusionId || !rowId || !assignedPackageIds || isInclusionHidden(inclusion)) {
        return [];
      }

      return assignedPackageIds.has(packageId) ? [] : [rowId];
    }),
  );
}

export function getPackageInclusionIdsForMasterInclusion(
  masterInclusion: MasterInclusion,
  packageInclusions: Record<string, Array<OfferPackageInclusionRecord>> | undefined,
): Array<string> {
  if (!masterInclusion.persistId || !packageInclusions) {
    return [];
  }

  return Object.values(packageInclusions).flatMap((inclusions) =>
    inclusions.flatMap((rawInclusion) => {
      const inclusion = rawInclusion as OfferPackageInclusionWithDisplayFields;
      const rowId = rawInclusion.id ? String(rawInclusion.id) : undefined;

      return rowId && getInclusionPersistId(inclusion) === masterInclusion.persistId && !isInclusionHidden(inclusion)
        ? [rowId]
        : [];
    }),
  );
}
